import { EDU_BASENAME, resolveRouterBasename } from './appBase';

/**
 * Exact-match detector for the edu portal apex landing page.
 * Deliberately strict (no substring/startsWith matching) so a routing
 * decision can never accidentally swallow an unrelated host.
 * edu.localhost is included for local development, following the same
 * *.localhost convention already used elsewhere in this codebase (see
 * run.py's CORS config and its lnat.localhost:3002 reference).
 * pathbuddy.com / www.pathbuddy.com, ivoriedu.com / www.ivoriedu.com and
 * edu.ivoriai.com serve the same edu portal as edu.ivori.ai. Neither
 * ivoriedu.com nor edu.ivoriai.com is Pathbuddy.
 *
 * This is pure host detection. The edu app now also runs under /edu on
 * ivoriai.com; code that means "behave as the edu portal" should call
 * isEduPortal(), which adds that case. The retired edu hosts stay in this
 * list because the host-redirect fallback and pathbuddy still key off it.
 */
export function isEduApexHost(hostname) {
  const h = (hostname || '').toLowerCase();
  return (
    h === 'edu.ivori.ai' ||
    h === 'www.edu.ivori.ai' ||
    h === 'edu.ivoriai.com' ||
    h === 'www.edu.ivoriai.com' ||
    h === 'ivoriedu.com' ||
    h === 'www.ivoriedu.com' ||
    h === 'edu.localhost' ||
    h === 'pathbuddy.com' ||
    h === 'www.pathbuddy.com'
  );
}

/** Pathbuddy.com twin of the edu portal — branding/catalogue overrides only. */
export function isPathbuddyHost(hostname) {
  const h = (hostname || (typeof window !== 'undefined' ? window.location.hostname : '') || '').toLowerCase();
  return h === 'pathbuddy.com' || h === 'www.pathbuddy.com';
}

/**
 * Is this page the edu portal? True on the edu hosts above (including
 * pathbuddy.com), on a path-prefix host (ivoriai.com, localhost, ...) when
 * the path is /edu or /edu/..., and on ivoriai.com/ (the Edu home). False on
 * the company home at ivoriai.com/about, on /education, on csuite hosts and
 * on exam hosts.
 * hostname and pathname default to the page that is loaded.
 */
export function isEduPortal(hostname, pathname) {
  const loc = typeof window !== 'undefined' && window.location ? window.location : {};
  const h = hostname === undefined ? loc.hostname : hostname;
  const p = pathname === undefined ? loc.pathname : pathname;
  return isEduApexHost(h) || resolveRouterBasename(h, p) === EDU_BASENAME;
}
